import {
  ComponentType,
  createContext,
  createElement,
  PropsWithChildren,
} from 'react';

import { InstructionsSection } from './InstructionsSection';
import { CastctlFeatureSelection } from './types';

/**
 * Extension point for the right-hand "connect instructions" area of the
 * shared `CastctlOnboardingDialog`.
 *
 * `SetupStep` consumes this context and renders whatever component the
 * provider supplies, passing it the same `featureSelection` it received. The
 * default value is the standard `InstructionsSection`, which preserves the
 * original main-console behaviour byte-for-byte when no provider is mounted.
 *
 * OneClick wraps the app with `CastctlConnectInstructionsProvider` to swap
 * `InstructionsSection` for the multi-method tabs component (`castctl |
 * Helm | Terraform`) without touching any of the dialog's call sites
 * (`useOnboardClusterDialog`, `useReconnectClusterDialog`,
 * `useEnableCastAiButtonState`, `ReliabilityMetricsNotInstalled`, etc.).
 */
export type CastctlConnectInstructionsComponent = ComponentType<{
  featureSelection: CastctlFeatureSelection;
}>;

const defaultRender: CastctlConnectInstructionsComponent = ({
  featureSelection,
}) => createElement(InstructionsSection, { featureSelection });

export const CastctlConnectInstructionsContext =
  createContext<CastctlConnectInstructionsComponent>(defaultRender);

type CastctlConnectInstructionsProviderProps = PropsWithChildren<{
  value: CastctlConnectInstructionsComponent;
}>;

/**
 * Wraps the application with a custom `CastctlConnectInstructionsContext`
 * value. The supplied component is rendered in place of the default
 * `InstructionsSection` for every entry point of the shared
 * `CastctlOnboardingDialog`.
 */
export const CastctlConnectInstructionsProvider = ({
  value,
  children,
}: CastctlConnectInstructionsProviderProps) => (
  <CastctlConnectInstructionsContext.Provider value={value}>
    {children}
  </CastctlConnectInstructionsContext.Provider>
);
